import { FC, useEffect, useState } from 'react';
import { Alert, Button, Col, Form, InputGroup, Row } from 'react-bootstrap';

import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { changeLanguage, changeScreenState, changeThirdLanguageEnabled, loginAsync, registerAsync, selectLoginIsRegistered, selectLoginStatus, selectScreenState, selectUserLanguage } from './loginSlice';
import { getSurveyMetadata } from '../survey/surveyAPI';
import { SurveyMetadata } from '../survey/models/SurveyMetadata';
import { LoadingTransition } from '../../shared/components/LoadingTransition/LoadingTransition';
import { Navigate, useParams } from 'react-router-dom';
import { ResourcesService } from '../../shared/services/ResourcesService';
import { WelcomeScreen } from './WelcomeScreen';

type LoginProps = {
    isRegister?: boolean,
    toShow?: boolean,
};

export const Login: FC<LoginProps> = ({ isRegister, toShow }) => {
    const dispatch = useAppDispatch();
    const loginStatus = useAppSelector(selectLoginStatus);
    const isRegistered = useAppSelector(selectLoginIsRegistered);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [phoneCode, setPhoneCode] = useState('');
    const [phoneCodeError, setPhoneCodeError] = useState('');
    const [countryCode, setCountryCode] = useState('+359');
    const lang = useAppSelector(selectUserLanguage);
    const { surveyId } = useParams();
    const { companyId } = useParams();
    const screenState = useAppSelector(selectScreenState);
    const [registerTimeout, setRegisterTimeout] = useState(false);
    const [surveyMetadata, setSurveyMetadata] = useState<SurveyMetadata | null>(null);

    useEffect(() => {
        // Fetch survey metadata on page load anonymously and store it
        if (surveyId) {
            getSurveyMetadata(surveyId)
                .then((res) => {
                    const metadata = res?.data as SurveyMetadata | null;
                    setSurveyMetadata(metadata ?? null);
                    // store whether third language is enabled for this survey
                    dispatch(changeThirdLanguageEnabled(metadata?.thirdLanguageEnabled ?? false));

                    switch (metadata?.thirdLanguageCode) {
                        case "EN":
                            dispatch(changeLanguage('EN'));
                            break;
                        case "RO":
                            dispatch(changeLanguage('RO'));
                            break;
                        default:
                            break;
                    }
                })
                .catch((err) => {
                    setSurveyMetadata(null);
                    dispatch(changeThirdLanguageEnabled(false));
                });
        }

        dispatch(changeScreenState((!!companyId && !!surveyId) ? 'home' : 'login'));   
    }, [companyId, dispatch, surveyId])

    const onLogin = () => {
        if (phoneNumber.startsWith('0')) {
            setPhoneError(ResourcesService.translate('Phone number must not start with 0.', lang));
            return;
        }
        if (!(phoneNumber.length === 9 || phoneNumber.length === 10)) {
            setPhoneError(ResourcesService.translate('Invalid phone number.', lang));
            return;
        }
        setPhoneError('');

        if (phoneCode.length !== 4) {
            setPhoneCodeError(ResourcesService.translate('Invalid phone code.', lang));
            return;
        }
        setPhoneCodeError('');

        dispatch(loginAsync({ phone: phoneNumber, code: phoneCode }));
        dispatch(changeScreenState('survey'));
    };

    const onRegister = () => {
        if (!(phoneNumber.length === 9 || phoneNumber.length === 10)) {
            setPhoneError(ResourcesService.translate('Invalid phone number.', lang));
            return;
        }

        setRegisterTimeout(true);

        setTimeout(() => {
            setRegisterTimeout(false);
        }, 60000); // Timeout between new sms codes

        const fullPhoneNumber = `${countryCode}${phoneNumber}`;
        dispatch(registerAsync({ phone: fullPhoneNumber, nationalPhone: phoneNumber }));
    };

    const onNext = () => {
        //   surveyId === '08de122f-d60c-402f-8bf9-2b0c989061d7' || // 8.11.25  Romania HR survey
        //     surveyId === '08ddaf40-11fc-4cb0-8348-2547b7b564cd' ||
        //     surveyId === '08ddb886-6116-4385-8941-c7358bd95c5c' ||
        //     surveyId === '08ddb886-9330-4dad-82db-2c28169361d1' ||
        if (surveyMetadata && surveyMetadata.requiresLogin === false) {
            setPhoneCodeError('');
            dispatch(loginAsync({ phone: '012345689', code: 'zzzz' }));
            dispatch(changeScreenState('survey'));
        } else {
            dispatch(changeScreenState('login'))
        }
    }

    return isRegister && isRegistered
        ? <Navigate to={'/'} />
        : screenState === 'home'
            ? <WelcomeScreen onNext={() => onNext()} requiresLogin={surveyMetadata?.requiresLogin} />
            : (
                <Row className='bw-loginForm'>
                    <Col md={{ span: 5 }}>
                        <LoadingTransition cssClass='mt-4 p-5' status={loginStatus} lang={lang}>
                            <Form>
                                <Form.Group className="mb-3" controlId="formPhoneNumber">
                                    <Row>
                                        <Col>
                                            <h1>{ResourcesService.translate('Log in', lang)}</h1>
                                        </Col>
                                    </Row>
                                    <Row className='mb-3'>
                                        <Col>
                                            <span className='bw-introText'>{ResourcesService.translate('Please enter your phone number in the field and press SEND CODE. You will receive an SMS code on this phone that you will have to enter in the PHONE CODE field. Following these steps, click the LOG IN button to load the questionnaire.', lang)}</span>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col>
                                            <span className='bw-inputHeader'>{ResourcesService.translate('Phone number', lang)}</span>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col>
                                            <InputGroup className="mb-3">
                                                <Form.Select
                                                    aria-label="Country code selector"
                                                    value={countryCode}
                                                    onChange={e => setCountryCode(e.target.value)}
                                                    disabled={loginStatus === 'loading'}
                                                    className="country-code-selector"
                                                >
                                                    <option value="359">+359</option>
                                                    <option value="30">+30</option>
                                                    <option value="40">+40</option>
                                                    <option value="90">+90</option>
                                                    <option value="353">+353</option>
                                                </Form.Select>
                                                <Form.Control
                                                    disabled={loginStatus === 'loading'}
                                                    type="tel"
                                                    placeholder={ResourcesService.translate('Specify here without \'0\'', lang)}
                                                    value={phoneNumber}
                                                    onChange={e => setPhoneNumber(e.target.value)}
                                                />
                                                {
                                                    phoneError &&
                                                    <Alert variant='warning'>{phoneError}</Alert>
                                                }
                                            </InputGroup>
                                        </Col>
                                    </Row>
                                </Form.Group>
                                <Form.Group className="mb-3" controlId="formPhoneCode">
                                    <Row>
                                        <Col>
                                            <span className='bw-inputHeader'>{ResourcesService.translate('Phone code', lang)}</span>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col>
                                            <Form.Control type="password" disabled={loginStatus === 'loading'} placeholder={ResourcesService.translate('Specify here', lang)} value={phoneCode} onChange={e => setPhoneCode(e.target.value)} />
                                            {
                                                phoneCodeError &&
                                                <Alert variant='warning'>{phoneCodeError}</Alert>
                                            }
                                        </Col>
                                    </Row>
                                </Form.Group>
                                <Row className='mb-3'>
                                    <Col>
                                        <span className='bw-introText'>{ResourcesService.translate('In case you do not receive an SMS within 3 minutes, the BeyondWork team is available to assist you at ', lang)}</span><span className='bw-introText'><a href="mailto:hello@beyondwork.bg">hello@beyondwork.bg</a></span><span className='bw-introText'>{ResourcesService.translate(' or at ', lang)}</span><span className='bw-introText'><a href="tel:+359897505478">0897 505 478</a></span>
                                    </Col>
                                </Row>
                                <Row className='mt-3'>
                                    <Col sm='6'>
                                        <Button className='btn btn-sendCode' variant="secondary" disabled={loginStatus === 'loading' || registerTimeout} type="button" onClick={() => onRegister()}>
                                            {ResourcesService.translate('Send Code', lang)}
                                            <i className='bi bi-chevron-right'></i>
                                        </Button>
                                    </Col>
                                    <Col sm='6'>
                                        <Button className='btn btn-login' variant="primary" disabled={loginStatus === 'loading'} type="button" onClick={() => onLogin()}>
                                            {ResourcesService.translate('Log in', lang)}
                                            <i className='bi bi-chevron-right'></i>
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </LoadingTransition>
                    </Col>
                </Row >
            );
}
